import React from 'react';

interface RegistryHelpProps {
  subTab: string;
}

/**
 * Registry Help Content - Prompts, Datasets, Vector Exports
 */
const RegistryHelp: React.FC<RegistryHelpProps> = ({ subTab }) => {
  if (subTab === 'datasets') {
    return <DatasetsHelp />;
  }
  if (subTab === 'vector-exports') {
    return <VectorExportsHelp />;
  }
  return <PromptsHelp />;
};

const PromptsHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Prompts
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        The Prompt Registry is your central hub for managing LLM prompts. Version control your prompts, promote them through environments, and track changes over time.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Creating Prompts
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Click <span className="font-medium">Create Prompt</span> to start a new prompt</p>
        <p>&#8226; Define the prompt template with variables using <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{'{{variable}}'}</code> syntax</p>
        <p>&#8226; Add metadata and tags for organization</p>
        <p>&#8226; Test your prompt before saving</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Version Control
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
        Every change creates a new version:
      </p>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Draft</span>: Work in progress, not deployed</p>
        <p>&#8226; <span className="font-medium">Staging</span>: Ready for testing</p>
        <p>&#8226; <span className="font-medium">Production</span>: Live in your applications</p>
        <p>&#8226; View version history and compare changes</p>
        <p>&#8226; Rollback to previous versions if needed</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Promotion Workflow
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Promote prompts through environments using the governance workflow. Configure approval requirements and policies under the Governance tab.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Version Labels
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
        Assign labels like <span className="font-medium">stable</span> or <span className="font-medium">latest</span> to specific prompt versions. Labels let your agents always fetch the right version without hardcoding version numbers.
      </p>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Click <span className="font-medium">Set as Stable</span> in the version history to assign the label</p>
        <p>&#8226; Only one version per label per prompt and environment</p>
        <p>&#8226; Reassigning a label automatically removes it from the previous version</p>
        <p>&#8226; Labels sync to Bedrock as tags (e.g., <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">aivilo:label=stable</code>)</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Resolve by Label
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
        Agents can resolve prompts by label instead of version number using the resolve endpoint:
      </p>
      <div className="bg-gray-100 dark:bg-gray-800 rounded p-2 mb-3">
        <code className="text-xs text-gray-700 dark:text-gray-300">
          GET /prompts/resolve?name=X&environment=Y&label=stable
        </code>
      </div>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Returns the specific version number and Bedrock ARN</p>
        <p>&#8226; On rollback, the label moves to the previous version automatically</p>
        <p>&#8226; Agents pick up the change without any code modifications</p>
      </div>
    </div>

  </>
);

const DatasetsHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Datasets
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Datasets store test cases for evaluating your prompts. Create collections of inputs and expected outputs to measure prompt quality and catch regressions.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Dataset Types
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Q&A Pairs</span>: Input questions with expected answers</p>
        <p>&#8226; <span className="font-medium">Test Cases</span>: Complex scenarios with multiple assertions</p>
        <p>&#8226; <span className="font-medium">Documents</span>: Reference documents for RAG evaluation</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Managing Datasets
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Add items manually or import from CSV/JSON</p>
        <p>&#8226; Edit and organize test cases</p>
        <p>&#8226; Tag datasets for filtering</p>
        <p>&#8226; Link datasets to specific prompts</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Using Datasets
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Run evaluations against datasets from the Evaluation tab. Compare results across prompt versions to ensure quality improvements.
      </p>
    </div>

  </>
);

const VectorExportsHelp = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Vector Exports
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Vector Exports allow you to export embeddings to cloud-based vector stores for RAG (Retrieval Augmented Generation) applications.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Supported Destinations
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Amazon Bedrock Knowledge Bases</span></p>
        <p>&#8226; <span className="font-medium">Pinecone</span></p>
        <p>&#8226; <span className="font-medium">Weaviate</span></p>
        <p>&#8226; <span className="font-medium">Custom endpoints</span></p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Creating Exports
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Select source documents or data</p>
        <p>&#8226; Choose embedding model and chunk settings</p>
        <p>&#8226; Configure destination credentials</p>
        <p>&#8226; Schedule automatic sync or run manually</p>
      </div>
    </div>

  </>
);

export default RegistryHelp;
